import type { ReactNode } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface StatsCardProps {
  title: string;
  value: string;
  icon: ReactNode;
  iconBg?: string;
  trend?: number;       // percentage change, positive = up
  trendLabel?: string;
  loading?: boolean;
}

export default function StatsCard({
  title,
  value,
  icon,
  iconBg = 'bg-brand-500/15',
  trend,
  trendLabel,
  loading = false,
}: StatsCardProps) {
  const trendUp = (trend ?? 0) >= 0;

  return (
    <div className="card p-5 flex flex-col gap-4">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center`}>
          {icon}
        </div>
        {trend !== undefined && (
          <div
            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
              trendUp
                ? 'bg-red-500/10 text-red-400'   // more spending = bad
                : 'bg-emerald-500/10 text-emerald-400' // less spending = good
            }`}
          >
            {trendUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {Math.abs(trend).toFixed(1)}%
          </div>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">
          <div className="h-7 w-24 bg-slate-800 rounded-lg animate-pulse" />
          <div className="h-4 w-32 bg-slate-800 rounded animate-pulse" />
        </div>
      ) : (
        <div>
          <p className="text-2xl font-bold text-white">{value}</p>
          <p className="text-sm text-slate-400 mt-0.5">
            {title}
            {trendLabel && <span className="text-slate-500"> · {trendLabel}</span>}
          </p>
        </div>
      )}
    </div>
  );
}
